import { IPlayerDebugSettingsGES } from '../debugging';
import { IDeviceInfo } from '../devices';
import { CompletePartDataSet } from '../entities';
import { ERoleHatItemId } from '../enums';
import { ITiledMap } from '../game/tiled.types';
import { ISocket } from '../network';
import { INovaUserAuthHeaders, UUID } from '../users';

export enum PlayerStatus {
  PENDING = 'pending',
  IN_LOBBY = 'in_lobby',
  IN_QUEUE = 'in_queue',
  PLAYING = 'playing'
}

export interface ILobbyPlayerPersistent {
  id: UUID;
  isReadyState: boolean;
  status?: PlayerStatus;
}
// TODO: Segregate id, socketId, etc. Leave only the "base" (client-only) and "extended" (server-side props) versions of ILobbyPlayer.
// ^ This depends on how individuals are updated. If one lobby-player buys a boost, if not by user id,
// do we find & update that player on each other client just by array index? Or re-send the entire lobby structure for update? (Unnecessary overhead?)
export interface ILobbyPlayerMatchPrep extends ILobbyPlayerPersistent {
  nickname: string;
  locale: string;
  level: number;
  team: number;
  slot: number;
  selectedBoostId?: string;
  selectBoostOnPurchase?: boolean;

  parts: CompletePartDataSet;
  roleSkillDatasetKey: ERoleHatItemId;

  /**
   * This will make it clear for each individual user who is in a match, even if they are in a lobby with others
   * (since they might not all close the match scene at exactly the same time)
   */
  matchId?: UUID;

  socketId?: string;
  playerDebugSettingsGES?: IPlayerDebugSettingsGES; // optional, only when env.debugMode is true

  /** ISO 8601 UTC from Core when Play After GameShow is active for this player; MMS → GES for match timers. */
  playAfterGameShowUntil?: string | null;
}

export type ILobbyPlayer = ILobbyPlayerPersistent | ILobbyPlayerMatchPrep;

/**
 * This is used strictly on the GES, just adding a couple of props to ILobbyPlayerMatchPrep,
 * allowing the lobby player to transfer them to the Player class object, so long as they're used in there.
 */
export interface ILobbyPlayerMatchReady extends ILobbyPlayerMatchPrep {
  /**
   * This is the socket object that is transferred to the Player class through a few layers of Match preparation.
   * Despite having no direct usage here, it's the best way to accomodate both SP & MP modes,
   * without writing significantly more code to accomplish the same thing.
   */
  socket?: ISocket;
  authHeaders?: INovaUserAuthHeaders;
  deviceInfo?: IDeviceInfo;
}

export interface ILobbyCreateNewMatchParams {
  lobbyId: string;
  authHeaders: INovaUserAuthHeaders;
  deviceInfo: IDeviceInfo;
  /** When playing a sandbox map imported from folder; engine uses this instead of fetching map JSON. */
  preloadedMapData?: ITiledMap;
  /** ISO 8601 UTC from Core when Play After GameShow is active for this player; */
  playAfterGameShowUntil?: string | null;
}

export type ILobbyPlayerMatchPrepForClients = Omit<ILobbyPlayerMatchPrep, 'id' | 'socketId' | 'playerDebugSettingsGES' | 'deviceInfo'>;

/**
 * This represents a very minor subset of the data of "ILobbyPlayerMatchPrep" to update the MMS presence service.
 * Because it's so small right now, it's not being derived or using any TS extension.
 */
export interface ILobbyPlayerPresenceSubset {
  id: UUID;
}
